import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Button,
  Container,
  Form,
  FormControl,
  InputGroup,
  Nav,
  Navbar,
  ListGroup,
} from 'react-bootstrap';
import { FaSearch, FaShoppingCart } from 'react-icons/fa';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useSearch } from '../../context/SearchContext';
import { useCarrito } from '../../context/CarritoContext';
import { NavLink, useNavigate } from 'react-router-dom';
import { obtenerURLImagen } from '../../utils/imageUtils';
import CarritoSidebar from '../cart/CarritoSidebar';
import './MyNavbar.css';

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/fundas', label: 'Fundas' },
  { path: '/accesorios', label: 'Accesorios' },
  { path: '/vidrios', label: 'Vidrios' },
  { path: '/terminos-y-condiciones', label: 'Términos y Condiciones' },
];

const SearchForm = ({ className = '', onAfterNavigate = () => {} }) => {
  const { searchTerm, updateSearchTerm } = useSearch();
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [productos, setProductos] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();

  // Generar título dinámico
  const generarTitulo = useCallback((producto) => {
    const { modelo, material, color } = producto;
    const partes = [];

    if (modelo?.trim()) partes.push(modelo.trim());
    if (material?.trim()) partes.push(material.trim());
    if (color?.trim()) partes.push(color.trim());

    if (partes.length === 0) {
      return producto.nombre || 'Producto sin título';
    }

    return partes
      .map(parte => parte.charAt(0).toUpperCase() + parte.slice(1).toLowerCase())
      .join(' ');
  }, []);

  // Obtener productos de Firebase
  useEffect(() => {
    const obtenerProductos = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'Productos'));
        const productosObtenidos = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(p => p.imagenURL && p.precio);
        setProductos(productosObtenidos);
      } catch (error) {
        console.error('Error al obtener productos:', error);
      }
    };

    obtenerProductos();
  }, []);

  // Filtrar sugerencias
  const sugerencias = useMemo(() => {
    if (!localSearchTerm || localSearchTerm.trim().length < 2) return [];

    const terminoBusqueda = localSearchTerm.toLowerCase().trim();

    return productos
      .filter(producto => {
        const titulo = generarTitulo(producto).toLowerCase();
        const nombre = (producto.nombre || '').toLowerCase();
        const modelo = (producto.modelo || '').toLowerCase();
        const material = (producto.material || '').toLowerCase();
        const color = (producto.color || '').toLowerCase();
        const categoria = (producto.categoria || '').toLowerCase();

        return (
          titulo.includes(terminoBusqueda) ||
          nombre.includes(terminoBusqueda) ||
          modelo.includes(terminoBusqueda) ||
          material.includes(terminoBusqueda) ||
          color.includes(terminoBusqueda) ||
          categoria.includes(terminoBusqueda)
        );
      })
      .slice(0, 5);
  }, [productos, localSearchTerm, generarTitulo]);


  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (localSearchTerm.trim()) {
      updateSearchTerm(localSearchTerm.trim());
      setShowSuggestions(false);
      // Navegar a una lista para mostrar las cards filtradas
      navigate('/fundas');
      onAfterNavigate();
    }
  }, [localSearchTerm, updateSearchTerm, navigate, onAfterNavigate]);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setLocalSearchTerm(value);
    setShowSuggestions(value.trim().length >= 2);
  }, []);

  const handleSuggestionClick = useCallback((producto) => {
    const titulo = generarTitulo(producto);
    setLocalSearchTerm(titulo);
    updateSearchTerm(titulo);
    setShowSuggestions(false);
    // Redirigir según categoría del producto
    const categoria = (producto.categoria || '').toString().toLowerCase();
    if (categoria === 'fundas' || categoria === 'accesorios' || categoria === 'vidrios') {
      navigate(`/${categoria}`);
    } else {
      navigate('/fundas');
    }
    onAfterNavigate();
  }, [generarTitulo, updateSearchTerm, navigate, onAfterNavigate]);

  const handleBlur = useCallback(() => {
    setTimeout(() => setShowSuggestions(false), 200);
  }, []);

  const handleFocus = useCallback(() => {
    if (localSearchTerm.trim().length >= 2) {
      setShowSuggestions(true);
    }
  }, [localSearchTerm]);

  return (
    <div className="search-container position-relative">
      <Form className={`d-flex ${className}`} onSubmit={handleSubmit}>
        <InputGroup className="w-100">
          <FormControl
            type="search"
            placeholder="¿Qué estás buscando?"
            aria-label="Search"
            value={localSearchTerm}
            onChange={handleSearchChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
          />
              {localSearchTerm && (
                <button
                  type="button"
                  className="btn-close search-clear-btn"
                  aria-label="Limpiar búsqueda"
                  onClick={() => {
                    setLocalSearchTerm('');
                    updateSearchTerm('');
                    setShowSuggestions(false);
                  }}
                />
              )}
          <Button variant="outline-secondary" type="submit">
            <FaSearch />
          </Button>
        </InputGroup>
      </Form>

      {showSuggestions && sugerencias.length > 0 && (
        <div className="suggestions-container">
          <ListGroup className="suggestions-list">
            {sugerencias.map((producto) => (
              <ListGroup.Item
                key={producto.id}
                action
                onClick={() => handleSuggestionClick(producto)}
                className="suggestion-item"
              >
                <div className="suggestion-content">
                  <img
                    src={obtenerURLImagen(producto.imagenURL)}
                    alt={generarTitulo(producto)}
                    className="suggestion-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="suggestion-text">
                    <div className="suggestion-title">{generarTitulo(producto)}</div>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
    </div>
  );
};

function MyNavbar() {
  const [showMenu, setShowMenu] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const { cantidadTotal, showCarritoSidebar, setShowCarritoSidebar } = useCarrito();
  const toggleMenu = useCallback(() => {
    console.log('Toggle menu clicked, current state:', showMenu);
    setShowMenu(prev => !prev);
  }, [showMenu]);
  const closeMenu = useCallback(() => setShowMenu(false), []);
  const toggleCarrito = useCallback(() => setShowCarritoSidebar(prev => !prev), [setShowCarritoSidebar]);
  const closeCarrito = useCallback(() => setShowCarritoSidebar(false), [setShowCarritoSidebar]);
  const handleNavClick = useCallback(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    closeMenu();
  }, [closeMenu]);

  useEffect(() => {
    // Pequeño delay para que la animación sea visible
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Navbar expand="md" className={`bg-body-tertiary fixed-top shadow-sm ${isLoaded ? 'navbar-loaded' : ''}`}>
      <Container fluid className="nav-style">
        {/* Desktop */}
        <div className="d-none d-md-block position-relative w-100 desktop-top-bar">
          <Navbar.Brand as={NavLink} to="/" className="desktop-logo">
            <img src="/logo.png" alt="iCase Oeste" className="navbar-logo" />
          </Navbar.Brand>
          <div className="desktop-search-container">
            <SearchForm className="buscador-wrapper" onAfterNavigate={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />
          </div>
          <button className="desktop-cart-icon" onClick={toggleCarrito}>
            <FaShoppingCart />
            {cantidadTotal > 0 && <span className="cart-badge">{cantidadTotal}</span>}
          </button>
        </div>

        <hr className="separador-nav d-none d-md-block w-100" />

        <Nav className="justify-content-center d-none d-md-flex navlink-text">
          {navLinks.map(({ path, label }) => (
            <Nav.Link key={path} as={NavLink} to={path} onClick={handleNavClick}>{label}</Nav.Link>
          ))}
        </Nav>

        {/* Mobile */}
        <div className="d-md-none w-100 d-flex justify-content-between align-items-center mobile-bar">
          <Button
            variant="outline-secondary"
            className={`menu-btn ${showMenu ? 'active' : ''}`}
            onClick={toggleMenu}
            aria-label="Abrir menú"
            style={{ minWidth: '44px', minHeight: '44px' }}
          >
            <span className="navbar-toggler-icon" />
          </Button>
          <Navbar.Brand as={NavLink} to="/" className="m-0">
            <img src="/logo.png" alt="iCase Oeste" className="navbar-logo-mobile" />
          </Navbar.Brand>
          <button className="mobile-cart-icon" onClick={toggleCarrito}>
            <FaShoppingCart />
            {cantidadTotal > 0 && <span className="cart-badge">{cantidadTotal}</span>}
          </button>
        </div>

        {/* Mobile Menu Alternative */}
        {showMenu && (
          <div 
            className="mobile-menu-overlay d-md-none"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100vh',
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 9998,
            }}
            onClick={closeMenu}
          >
            <div 
              className="mobile-menu-content"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '80%',
                height: '100vh',
                backgroundColor: 'white',
                padding: '1rem',
                zIndex: 9999,
                overflowY: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <img src="/logo.png" alt="iCase Oeste" className="navbar-logo-mobile-menu" />
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={closeMenu}
                  aria-label="Cerrar"
                />
              </div>
              <SearchForm className="mb-3" onAfterNavigate={closeMenu} />
              <Nav className="flex-column text-start">
                {navLinks.map(({ path, label }) => (
                  <Nav.Link 
                    key={path} 
                    as={NavLink} 
                    to={path} 
                    onClick={handleNavClick}
                    className="py-2"
                  >
                    {label}
                  </Nav.Link>
                ))}
              </Nav>
            </div>
          </div>
        )}
      </Container>

      {/* Carrito Sidebar */}
      <CarritoSidebar show={showCarritoSidebar} handleClose={closeCarrito} />
    </Navbar>
  );
}

export default MyNavbar;
